import React, { useState, useEffect, useCallback } from "react";
import { Plus, Users, UserPlus, UserMinus, Edit2, Power } from "lucide-react";
import { LoginUserData } from "../LoginForm/LoginForm";
import { realAPI } from "../../services/realAPI";
import { useNotifications } from "../NotificationSystem/NotificationSystem";

interface TeamManagementProps {
  currentUser: LoginUserData;
  onStatsUpdate: () => void;
}

interface Team {
  id: string | number;
  organizationId: string | number;
  name: string;
  description: string;
  memberIds: (string | number)[];
  adminIds: (string | number)[];
  createdAt: Date;
  createdBy: string;
  enabled?: boolean;
}

interface User {
  id: string | number;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  role: "admin" | "member";
  teamIds: (string | number)[];
}

const TeamManagement: React.FC<TeamManagementProps> = ({
  currentUser,
  onStatsUpdate,
}) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [userToRemove, setUserToRemove] = useState<{
    teamId: string;
    userId: string;
    userName: string;
  } | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemoving, setIsRemoving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [editFormData, setEditFormData] = useState({
    name: "",
    description: "",
  });
  const { success, error: showError } = useNotifications();

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [teamsData, usersData] = await Promise.all([
        realAPI.getTeamsByOrganization(currentUser.organizationId),
        realAPI.getUsersByOrganization(currentUser.organizationId),
      ]);

      // Debug logging to understand data structure
      console.log("🔍 Teams data:", teamsData);
      console.log("🔍 Users data:", usersData);

      if (teamsData && teamsData.length > 0) {
        console.log("🔍 Sample team:", teamsData[0]);
        console.log("🔍 Sample team memberIds:", teamsData[0]?.memberIds);
        console.log(
          "🔍 Sample team enabled field:",
          teamsData[0]?.enabled,
          typeof teamsData[0]?.enabled
        );

        // Check all teams for enabled field
        teamsData.forEach((team: any, index: number) => {
          console.log(
            `🔍 Team ${index} - ${team.name}: enabled = ${
              team.enabled
            } (${typeof team.enabled})`
          );
        });
      }

      if (usersData && usersData.length > 0) {
        console.log("🔍 Sample user:", usersData[0]);
        console.log(
          "🔍 Sample user id:",
          usersData[0]?.id,
          typeof usersData[0]?.id
        );
      }

      setTeams(teamsData || []);
      setUsers(usersData || []);
    } catch (error) {
      console.error("Error loading teams:", error);
      setTeams([]);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser.organizationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load only teams data (for when users data doesn't need refreshing)
  const loadTeamsData = async () => {
    try {
      const teamsData = await realAPI.getTeamsByOrganization(
        currentUser.organizationId
      );
      console.log("🔍 Refreshed teams data:", teamsData);

      if (teamsData && teamsData.length > 0) {
        // Check all teams for enabled field
        teamsData.forEach((team: any, index: number) => {
          console.log(
            `🔍 Refreshed Team ${index} - ${team.name}: enabled = ${
              team.enabled
            } (${typeof team.enabled})`
          );
        });
      }

      setTeams(teamsData || []);
    } catch (error) {
      console.error("Error loading teams:", error);
      setTeams([]);
    }
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await realAPI.createTeam(currentUser.organizationId, {
        name: formData.name,
        description: formData.description,
        createdBy: currentUser.id,
      });

      setFormData({ name: "", description: "" });
      setShowCreateForm(false);
      await loadData();
      onStatsUpdate();
      success(
        "Team Created",
        `Team "${formData.name}" has been created successfully.`
      );
    } catch (error) {
      console.error("Error creating team:", error);
      showError("Creation Failed", "Failed to create team. Please try again.");
    }
  };

  const handleEditTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeam) return;

    try {
      await realAPI.updateTeam(String(selectedTeam.id), {
        name: editFormData.name,
        description: editFormData.description,
      });

      setShowEditModal(false);
      setSelectedTeam(null);
      await loadData();
      success(
        "Team Updated",
        `Team "${editFormData.name}" has been updated successfully.`
      );
    } catch (err) {
      console.error("Error updating team:", err);
      showError("Update Failed", "Failed to update team. Please try again.");
    }
  };

  const openEditModal = (team: Team) => {
    setSelectedTeam(team);
    setEditFormData({
      name: team.name,
      description: team.description,
    });
    setShowEditModal(true);
  };

  const toggleTeamStatus = async (team: Team) => {
    console.log("🔄 Toggling team status for:", team.name);
    console.log(
      "🔍 Current team.enabled value:",
      team.enabled,
      typeof team.enabled
    );
    console.log("🔍 Full team object:", team);

    try {
      // Handle undefined as enabled (default behavior)
      const currentStatus = team.enabled === false ? false : true;
      const newStatus = !currentStatus;
      console.log(
        "🎯 Current status:",
        currentStatus,
        "-> New status:",
        newStatus
      );

      await realAPI.updateTeam(String(team.id), {
        enabled: newStatus,
      });

      console.log("✅ API call successful, reloading teams data only...");
      await loadTeamsData(); // Only reload teams, not organization/users data
      success(
        "Team Status Updated",
        `Team "${team.name}" has been ${newStatus ? "enabled" : "disabled"}.`
      );
    } catch (err) {
      console.error("Error updating team status:", err);
      showError(
        "Update Failed",
        "Failed to update team status. Please try again."
      );
    }
  };

  const handleAddUserToTeam = async (userId: string | number) => {
    if (!selectedTeam) return;

    try {
      const user = getUserById(String(userId));
      await realAPI.addUserToTeam(String(selectedTeam.id), String(userId));
      await loadData();
      setShowAddUserModal(false);
      setSelectedTeam(null);
      if (user) {
        success(
          "Member Added",
          `${user.firstName} ${user.lastName} has been added to ${selectedTeam.name}.`
        );
      }
    } catch (err) {
      console.error("Error adding user to team:", err);
      showError("Add Failed", "Failed to add team member. Please try again.");
    }
  };

  const handleRemoveUserFromTeam = async () => {
    if (!userToRemove) return;

    try {
      setIsRemoving(true);
      await realAPI.removeUserFromTeam(
        userToRemove.teamId,
        userToRemove.userId
      );
      await loadData();
      setShowRemoveConfirm(false);
      setUserToRemove(null);
      success(
        "Member Removed",
        `${userToRemove.userName} has been successfully removed from the team.`
      );
    } catch (err) {
      console.error("Error removing user from team:", err);
      showError(
        "Remove Failed",
        "Failed to remove team member. Please try again."
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const confirmRemoveUser = (
    teamId: string | number,
    userId: string | number
  ) => {
    const user = getUserById(String(userId));
    if (user) {
      setUserToRemove({
        teamId: String(teamId),
        userId: String(userId),
        userName: `${user.firstName} ${user.lastName}`,
      });
      setShowRemoveConfirm(true);
    }
  };

  const getUserById = (userId: string) => {
    const user = users.find((user) => String(user.id) === String(userId));

    if (!user) {
      console.log("🔍 User not found for ID:", userId);
      console.log(
        "🔍 Available users:",
        users.map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}` }))
      );
    }

    return user;
  };

  // Helper function to determine if team is disabled
  const isTeamDisabled = (team: Team) => {
    return team.enabled === false;
  };

  const getAvailableUsers = (team: Team) => {
    return users.filter(
      (user) =>
        !team.memberIds.some((memberId) => String(memberId) === String(user.id))
    );
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Team Management</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Team
        </button>
      </div>

      {/* Create Team Form */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Create New Team
            </h3>
            <form onSubmit={handleCreateTeam}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Create Team
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Edit Team
            </h3>
            <form onSubmit={handleEditTeam}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Name
                  </label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedTeam(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Update Team
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User to Team Modal */}
      {showAddUserModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Add User to {selectedTeam.name}
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {getAvailableUsers(selectedTeam).map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleAddUserToTeam(user.id)}
                  className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="font-medium">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                  <div className="text-sm text-gray-500">{user.jobTitle}</div>
                </button>
              ))}
              {getAvailableUsers(selectedTeam).length === 0 && (
                <p className="text-gray-500 text-center py-4">
                  No available users to add
                </p>
              )}
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setShowAddUserModal(false);
                  setSelectedTeam(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove User Confirmation Modal */}
      {showRemoveConfirm && userToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Remove Team Member
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove{" "}
              <strong>{userToRemove.userName}</strong> from this team? This
              action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRemoveConfirm(false);
                  setUserToRemove(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={isRemoving}
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveUserFromTeam}
                disabled={isRemoving}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed transition-colors"
              >
                {isRemoving ? "Removing..." : "Remove Member"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teams List */}
      <div className="grid gap-6">
        {teams.map((team) => (
          <div
            key={team.id}
            className={`bg-white rounded-lg shadow p-6 ${
              isTeamDisabled(team) ? "opacity-60 border-l-4 border-red-400" : ""
            }`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    {team.name}
                  </h3>
                  <span
                    className={`ml-3 px-2 py-1 text-xs rounded-full ${
                      isTeamDisabled(team)
                        ? "bg-red-100 text-red-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {isTeamDisabled(team) ? "Disabled" : "Enabled"}
                  </span>
                </div>
                <p className="text-gray-600 mt-1">{team.description}</p>
                <div className="flex items-center mt-2 text-sm text-gray-500">
                  <Users className="w-4 h-4 mr-1" />
                  {team.memberIds.length} members
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => toggleTeamStatus(team)}
                  className={`px-3 py-1 rounded text-sm transition-colors flex items-center ${
                    isTeamDisabled(team)
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-red-600 text-white hover:bg-red-700"
                  }`}
                  title={isTeamDisabled(team) ? "Enable team" : "Disable team"}
                >
                  <Power className="w-4 h-4 mr-1" />
                  {isTeamDisabled(team) ? "Enable" : "Disable"}
                </button>
                <button
                  onClick={() => openEditModal(team)}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors flex items-center"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    setSelectedTeam(team);
                    setShowAddUserModal(true);
                  }}
                  className={`px-3 py-1 rounded text-sm transition-colors flex items-center ${
                    isTeamDisabled(team)
                      ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                      : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                  disabled={isTeamDisabled(team)}
                  title={
                    isTeamDisabled(team)
                      ? "Enable team to add users"
                      : "Add user to team"
                  }
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Add User
                </button>
              </div>
            </div>

            {/* Team Members */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Team Members</h4>
              {isTeamDisabled(team) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                  <p className="text-yellow-800 text-sm">
                    ⚠️ This team is currently disabled. Members cannot access
                    team resources.
                  </p>
                </div>
              )}
              {team.memberIds.length > 0 ? (
                <div className="space-y-2">
                  {team.memberIds.map((userId) => {
                    const user = getUserById(String(userId));
                    if (!user) {
                      console.log("🔍 User not found for ID:", userId);
                      return null;
                    }

                    return (
                      <div
                        key={String(userId)}
                        className={`flex justify-between items-center p-3 bg-gray-50 rounded-lg ${
                          isTeamDisabled(team) ? "opacity-60" : ""
                        }`}
                      >
                        <div>
                          <div className="font-medium">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.jobTitle}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {team.adminIds.includes(userId) && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              Admin
                            </span>
                          )}
                          <button
                            onClick={() => confirmRemoveUser(team.id, userId)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove from team"
                            disabled={isTeamDisabled(team)}
                          >
                            <UserMinus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 italic">No members yet</p>
              )}
            </div>
          </div>
        ))}

        {teams.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No teams</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new team.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Team
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;
